import { createContext, useContext, useEffect } from "react";

interface PathTitle {
    pattern: RegExp;
    title: string;
}

export interface DocumentTitleContextValue {
    registerOverrides: (id: string, overrides: PathTitle[]) => void;
    unregisterOverrides: (id: string) => void;
}

export const DocumentTitleContext =
    createContext<DocumentTitleContextValue | null>(null);

/**
 * Hook for route groups to register document title overrides.
 * Overrides take priority over the global PATH_TITLES list.
 *
 * Must be rendered inside <DocumentTitleProvider>.
 *
 * The overrides array must be defined outside the component (or via useMemo)
 * to avoid unnecessary re-registrations.
 */
export function useDocumentTitleOverrides(
    id: string,
    overrides: PathTitle[],
): void {
    const context = useContext(DocumentTitleContext);
    if (!context) {
        throw new Error(
            "useDocumentTitleOverrides must be used within <DocumentTitleProvider>",
        );
    }

    useEffect(() => {
        context.registerOverrides(id, overrides);
        return () => context.unregisterOverrides(id);
    }, [id, overrides, context]);
}
